import { useMemo, useState } from 'react';
import { Box, Dialog, Stack } from '@mui/material';
import {
  StyledBox,
  StyledButton,
  StyledHeaderTypography,
  StyledShowsBox,
  StyledShowDateTypography,
  StyledShowBox,
  StyledShowLocationTypography,
  StyledShowVenueTypography,
  StyledShowArtistsTypography,
  StyledNoShowsBox,
} from './shows.styles';
import { SHOWS } from './shows.const';
import { APP_BAR_HEIGHT } from '../sticky-nav/sticky-nav.const';

export const Shows = () => {
  const [isPastShowsOpen, setIsPastShowsOpen] = useState(false);

  // Filter out past shows & sort by date
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0); // Ignore time, compare only date
    return d;
  }, []);
  const upcomingShows = useMemo(() => (
    SHOWS
      .filter((show) => {
        const [year, month, day] = show.date.split('-').map(Number);
        const showDate = new Date(year, month - 1, day);
        return showDate >= today;
      })
      .sort((a, b) => {
        const [ay, am, ad] = a.date.split('-').map(Number);
        const [by, bm, bd] = b.date.split('-').map(Number);
        const aDate = new Date(ay, am - 1, ad);
        const bDate = new Date(by, bm - 1, bd);
        return aDate.getTime() - bDate.getTime();
      })
  ), [today]);
  const pastShows = useMemo(() => (
    SHOWS
      .filter((show) => {
        const [year, month, day] = show.date.split('-').map(Number);
        const showDate = new Date(year, month - 1, day);
        return showDate < today;
      })
  ), [today]);

  const formattedDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // months are 0-indexed
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const handleDialogOpen = () => {
    setIsPastShowsOpen(true);
  }

  const handleDialogClose = () => {
    setIsPastShowsOpen(false);
  };

  return (
    <>
      <StyledBox
        id="shows"
        sx={{
          height: !!upcomingShows.length ? 'auto' : `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT}px)`,
          overflow: !!upcomingShows.length ? 'visible' : 'hidden',
        }}
      >
        <StyledShowsBox>
          <StyledHeaderTypography>Shows</StyledHeaderTypography>
          {!!upcomingShows.length && (
            <Stack sx={{ width: '100%' }}>
                {upcomingShows.map((show) => (
                  <StyledShowBox key={show.date + show.venue}>
                    <StyledShowDateTypography>{formattedDate(show.date)}</StyledShowDateTypography>
                    <Stack spacing={0.3}>
                      <StyledShowLocationTypography>{`${show.city}, ${show.state}`}</StyledShowLocationTypography>
                      <StyledShowVenueTypography>{show.venue}</StyledShowVenueTypography>
                      {!!show.artists?.length && (<StyledShowArtistsTypography>{`w/ ${show.artists.join(', ')}`}</StyledShowArtistsTypography>)}
                    </Stack>
                    {show.ticketUrl && (
                      <StyledButton
                        variant="contained"
                        component='a'
                        href={show.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Info
                      </StyledButton>
                    )}
                  </StyledShowBox>
                ))
              }
            </Stack>
          )}
          {!upcomingShows.length && (
            <StyledNoShowsBox>
              No upcoming shows
            </StyledNoShowsBox>
          )}
          <StyledButton variant="contained" onClick={(handleDialogOpen)}>Past Shows</StyledButton>
        </StyledShowsBox>
      </StyledBox>
      <Dialog onClose={handleDialogClose} open={isPastShowsOpen}>
        <Box sx={{ padding: 4 }}>
          <StyledHeaderTypography>Past Shows</StyledHeaderTypography>
          {!!pastShows.length && (
            <Stack sx={{ width: '100%' }}>
                {pastShows.map((show) => (
                  <StyledShowBox key={show.date + show.venue}>
                    <StyledShowDateTypography>{formattedDate(show.date)}</StyledShowDateTypography>
                    <Stack spacing={0.3}>
                      <StyledShowLocationTypography>{`${show.city}, ${show.state}`}</StyledShowLocationTypography>
                      <StyledShowVenueTypography>{show.venue}</StyledShowVenueTypography>
                      {!!show.artists?.length && (<StyledShowArtistsTypography>{`w/ ${show.artists.join(', ')}`}</StyledShowArtistsTypography>)}
                    </Stack>
                  </StyledShowBox>
                ))
              }
            </Stack>
          )}
        </Box>
      </Dialog>
    </>
  );
};
